import { cn } from "@/utils";
import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes, ReactNode } from "react";

/**
 * Selo de estado (status de cobrança, plano, etiqueta).
 *
 * Regra: o selo **sempre** carrega texto. Cor é reforço, nunca a informação —
 * daltônicos e telas sob sol forte não leem cor. Por isso não existe variante
 * "só bolinha colorida".
 */
const badgeVariants = cva(
  "inline-flex items-center gap-1.5 border font-ds-sans font-medium whitespace-nowrap",
  {
    variants: {
      tone: {
        neutral: "border-ds-ink-200 bg-ds-ink-100 text-ds-ink-700",
        brand: "border-ds-brand-200 bg-ds-brand-50 text-ds-brand-700",
        secondary:
          "border-ds-secondary-200 bg-ds-secondary-50 text-ds-secondary-700",
        success: "border-ds-success-200 bg-ds-success-50 text-ds-success-700",
        warning: "border-ds-warning-200 bg-ds-warning-50 text-ds-warning-700",
        danger: "border-ds-danger-200 bg-ds-danger-50 text-ds-danger-700",
      },
      solid: {
        true: "border-transparent",
        false: "",
      },
      /**
       * `tag` é o selo de estado — canto discreto, para não competir com o
       * conteúdo da linha. `pill` é a cápsula, que o olho lê como algo que se
       * escolhe: filtro, aba de contagem, categoria.
       */
      shape: {
        tag: "rounded-ds-sm",
        pill: "rounded-full",
      },
      size: {
        sm: "px-2 py-0.5 text-ds-caption",
        /** Alvo de toque de 44px — obrigatório quando a cápsula é clicável. */
        md: "min-h-11 px-4 text-ds-small",
      },
    },
    compoundVariants: [
      {
        solid: true,
        tone: "neutral",
        className: "bg-ds-ink-700 text-ds-on-solid",
      },
      {
        solid: true,
        tone: "brand",
        className: "bg-ds-brand-600 text-ds-on-solid",
      },
      {
        solid: true,
        tone: "secondary",
        className: "bg-ds-secondary-700 text-ds-on-solid",
      },
      {
        solid: true,
        tone: "success",
        className: "bg-ds-success-700 text-ds-on-solid",
      },
      {
        solid: true,
        tone: "warning",
        className: "bg-ds-warning-700 text-ds-on-solid",
      },
      {
        solid: true,
        tone: "danger",
        className: "bg-ds-danger-700 text-ds-on-solid",
      },
    ],
    defaultVariants: {
      tone: "neutral",
      solid: false,
      shape: "tag",
      size: "sm",
    },
  }
);

export interface BadgeProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, "color">,
    VariantProps<typeof badgeVariants> {
  icon?: ReactNode;
  children: ReactNode;
}

export const Badge = ({
  tone,
  solid,
  shape,
  size,
  icon,
  className,
  children,
  ...props
}: BadgeProps) => (
  <span
    className={cn(badgeVariants({ tone, solid, shape, size }), className)}
    {...props}
  >
    {icon ? (
      <span aria-hidden="true" className="[&_svg]:size-3">
        {icon}
      </span>
    ) : null}
    {children}
  </span>
);
